import React from "react";
import { ApiContext } from "./ApiContext";
import {
  CreateCustomFlashpackResponse,
  LeaderboardsResponse,
  CustomFlashpack,
  FilterInfo,
  FlashpackItem,
  FlashpackSubject,
  FlashpackType,
  Lesson,
  LessonAward,
  LessonOrUnitGroup,
  LessonsInProgress,
  LessonRecording,
  MainUnits,
  ManageSubscriptionUrlResponse,
  ModifyCustomFlashpackRequest,
  NextLessonResponse,
  PracticeMetadataResult,
  Profile,
  SchoolDashboard,
  SchoolDashboardError,
  SchoolGroupList,
  SchoolInsightReportList,
  SchoolList,
  SchoolRenewalList,
  SpeakingRate,
  TeacherAvatarInfo,
  TeacherLessonAssignments,
  TeacherSectionStudentList,
  UnitWithLessons,
  User,
  DeleteAccountResponse,
  StripeSubscriptionProduct,
  StripeSubscriptionProductId,
  StripeCheckoutSession,
  ReconcileStripeCheckoutSessionResult,
  FeedbackRequest,
  SchoolTeacher,
  CustomSection,
  MilestonesResponse,
  LessonStandardsResponse,
  SearchResponse,
  StandardsAttributionsResponse,
} from "../api";
import { RetryContext } from "./RetryContext";
import { AccountContext, RetryBehavior } from "./AccountContext";

interface Props {
  children: React.ReactNode;
}
export const ApiProvider = (props: Props) => {
  const { showRetry } = React.useContext(RetryContext);
  const { makeRequest } = React.useContext(AccountContext);

  const getCourses = React.useCallback((flags: { [key: string]: string }): Promise<{
    units: MainUnits;
    featured_units: MainUnits;
    filter_info: FilterInfo;
  }> => {
    const queryString = new URLSearchParams(flags).toString();
    const path = queryString ? `courses/?${queryString}` : "courses/";
    return makeRequest(
      path,
      "GET",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const obtainNextLesson = React.useCallback(
    (
      lessonId: number,
      profileId: number,
      version: number,
      customFlashpackId: number | null,
      lessonsInProgress: LessonsInProgress
    ): Promise<NextLessonResponse> => {
      let path = `next-lesson/${lessonId}/`;
      return makeRequest(
        path,
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          profile_id: profileId,
          version: version,
          custom_flashpack_id: customFlashpackId,
          lessons_in_progress: lessonsInProgress,
        }
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );
  const getLesson = React.useCallback(
    (
      lessonId: number,
      version: number | null,
      customFlashpackId: number | null
    ): Promise<Lesson> => {
      let path = `lessons/${lessonId}/?platform=webapp`;
      if (version !== null) {
        path += `&version=${version}`;
      }
      if (customFlashpackId !== null) {
        path += `&custom_flashpack_id=${customFlashpackId}`;
      }
      return makeRequest(
        path,
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getPracticeMetadata = React.useCallback(
    (
      profileId: number | null,
      lessonsInProgress: LessonsInProgress
    ): Promise<PracticeMetadataResult> => {
      const path = "practice/";
      return makeRequest(
        path,
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          profile_id: profileId,
          lessons_in_progress: lessonsInProgress,
        }
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getMyClassMetadata = React.useCallback(
    (
      profileId: number | null,
      lessonsInProgress: LessonsInProgress
    ): Promise<{
      lesson_assignments: [Lesson];
    }> => {
      const path = "my-class/teacher/";
      return makeRequest(
        path,
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          profile_id: profileId,
          lessons_in_progress: lessonsInProgress,
        }
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getHomeMetadata = React.useCallback(
    (
      profileId: number | null,
      lessonsInProgress: LessonsInProgress
    ): Promise<{
      groups: LessonOrUnitGroup[];
      filter_info: FilterInfo;
    }> => {
      const path = "home/";
      return makeRequest(
        path,
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          profile_id: profileId,
          lessons_in_progress: lessonsInProgress,
        }
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getStudentAssignmentsMetadata = React.useCallback(
    (
      profileId: number | null,
      teacherId: number | null,
      lessonsInProgress: LessonsInProgress
    ): Promise<{
      groups: [LessonOrUnitGroup];
    }> => {
      const path = `student-assignments/${teacherId}/`;
      return makeRequest(
        path,
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          profile_id: profileId,
          lessons_in_progress: lessonsInProgress,
        }
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getLeaderboards =
    React.useCallback((): Promise<LeaderboardsResponse> => {
      return makeRequest(
        "leaderboards/",
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => {
        return resp.body;
      });
    }, [makeRequest]);

  const getAwardsMetadata = React.useCallback(
    (
      profileId: number | null
    ): Promise<{
      unlocked_awards: [LessonAward];
    }> => {
      const path = `awards/${profileId}/`;
      return makeRequest(
        path,
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getMilestonesMetadata = React.useCallback(
    (profileId: number | null): Promise<MilestonesResponse> => {
      return makeRequest(
        `milestones/${profileId}/`,
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getUnitWithLessons = React.useCallback(
    (
      unitId: number,
      profileId: number | null,
      lessonsInProgress: LessonsInProgress
    ): Promise<{ unit: UnitWithLessons }> => {
      const path = `units/${unitId}/`;
      return makeRequest(
        path,
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          profile_id: profileId,
          lessons_in_progress: lessonsInProgress,
        }
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getSchoolDashboard = React.useCallback(
    (queryset: string): Promise<SchoolDashboard | SchoolDashboardError> => {
      const path = `schools/dashboard/${queryset}`;
      return makeRequest(
        path,
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getSchoolList = React.useCallback((): Promise<SchoolList> => {
    const path = `schools/all/`;
    return makeRequest(
      path,
      "GET",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const getSchoolInsightReports =
    React.useCallback((): Promise<SchoolInsightReportList> => {
      return makeRequest(
        "schools/insights/reports/",
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => resp.body);
    }, [makeRequest]);

  const getSchoolGroupList = React.useCallback((): Promise<SchoolGroupList> => {
    return makeRequest(
      "schools/groups/",
      "GET",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => resp.body);
  }, [makeRequest]);

  const getSchoolRenewalList =
    React.useCallback((): Promise<SchoolRenewalList> => {
      return makeRequest(
        "schools/renewals/",
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => {
        return resp.body;
      });
    }, [makeRequest]);

  const deleteLessonState = React.useCallback(
    (lessonId: number, version: number, profileId: number) => {
      const path = `lessons/${lessonId}/versions/${version}/delete-state/${profileId}/`;
      makeRequest(path, "DELETE", RetryBehavior.EXPOSE_ALL, true).catch(
        () => {}
      );
    },
    [makeRequest]
  );

  const makeGuestUser = React.useCallback(() => {
    // Make new user
    return makeRequest(
      "auth/registration/guest-user/",
      "POST",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      false
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const deleteCustomSection = React.useCallback(
    (schoolId: number, sectionId: number) => {
      const path = `schools/${schoolId}/sections/${sectionId}/`;
      makeRequest(
        path,
        "DELETE",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      );
    },
    [makeRequest]
  );

  const getCustomSection = React.useCallback(
    (schoolId: number, sectionId: number): Promise<CustomSection> => {
      const path = `schools/${schoolId}/sections/${sectionId}/`;
      return makeRequest(
        path,
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const updateCustomSection = React.useCallback(
    (
      schoolId: number,
      sectionId: number,
      name: string,
      student_profile_ids: number[],
      teacher_user_ids: number[]
    ): Promise<CustomSection> => {
      const path = `schools/${schoolId}/sections/${sectionId}/`;
      return makeRequest(
        path,
        "PATCH",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          name: name,
          student_profile_ids: student_profile_ids,
          teacher_user_ids: teacher_user_ids,
        }
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getCustomSections = React.useCallback(
    (schoolId: number): Promise<CustomSection[]> => {
      const path = `schools/${schoolId}/sections/`;
      return makeRequest(
        path,
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const createCustomSection = React.useCallback(
    (
      schoolId: number,
      name: string,
      student_profile_ids: number[],
      teacher_user_ids: number[]
    ): Promise<CustomSection> => {
      const path = `schools/${schoolId}/sections/`;
      return makeRequest(
        path,
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          name: name,
          student_profile_ids: student_profile_ids,
          teacher_user_ids: teacher_user_ids,
        }
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const getTeachers = React.useCallback((): Promise<{
    teachers: SchoolTeacher[];
  }> => {
    const path = "my-school/teacher-list/";
    return makeRequest(
      path,
      "GET",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const getTeacherSections = React.useCallback((): Promise<{
    sections: TeacherSectionStudentList[];
  }> => {
    const path = "my-class/student-list/";
    return makeRequest(
      path,
      "GET",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const createLessonAssignments = React.useCallback(
    (data: TeacherLessonAssignments) => {
      return makeRequest(
        "lesson-assignments/",
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        data
      );
    },
    [makeRequest]
  );

  const getSingleLessonAssignments = React.useCallback(
    (lessonId: number, customFlashpackId: number | null) => {
      const path =
        `lessons/${lessonId}/assignments/` +
        (customFlashpackId ? `?custom_flashpack_id=${customFlashpackId}` : "");
      return makeRequest(
        path,
        "GET",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  const deleteSingleLessonAssignments = React.useCallback(
    (
      lessonId: number,
      customFlashpackId: number | null,
      profileIdsToUnassign: number[]
    ) => {
      const path =
        `lessons/${lessonId}/assignments/delete/` +
        (customFlashpackId ? `?custom_flashpack_id=${customFlashpackId}` : "");
      return makeRequest(
        path,
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        { profile_ids: profileIdsToUnassign }
      );
    },
    [makeRequest]
  );

  const modifyProfile = React.useCallback(
    (
      profileId: number,
      firstName: string,
      firstNamePhonetics: string[],
      speakingRate: SpeakingRate,
      asrEnglishAccent: number,
      teacherAvatar: number,
      closedCaptionsEnabled: boolean,
      dontRetryErrors: boolean
    ): Promise<Profile | undefined> => {
      const path = `profiles/${profileId}/`;
      const body = {
        first_name: firstName,
        first_name_phonetics: firstNamePhonetics,
        speaking_rate: speakingRate,
        asr_english_accent: asrEnglishAccent,
        teacher_avatar: teacherAvatar,
        closed_captions_enabled: closedCaptionsEnabled,
      };
      const promise = makeRequest(
        path,
        "PATCH",
        dontRetryErrors
          ? RetryBehavior.EXPOSE_ALL
          : RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        body
      );
      return promise
        .then((resp) => {
          return resp.body;
        })
        .catch(() => {
          return undefined;
        });
    },
    [makeRequest]
  );

  const createProfile = React.useCallback(
    (
      firstName: string,
      firstNamePhonetics: string[],
      speakingRate: SpeakingRate,
      asrEnglishAccent: number,
      teacherAvatar: number,
      dontRetryErrors: boolean
    ): Promise<Profile | undefined> => {
      const path = "profiles/";
      const body = {
        first_name: firstName,
        first_name_phonetics: firstNamePhonetics,
        speaking_rate: speakingRate,
        asr_english_accent: asrEnglishAccent,
        teacher_avatar: teacherAvatar,
      };
      const promise = makeRequest(
        path,
        "POST",
        dontRetryErrors
          ? RetryBehavior.EXPOSE_ALL
          : RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        body
      );
      return promise
        .then((resp) => {
          return resp.body;
        })
        .catch(() => {
          return undefined;
        });
    },
    [makeRequest]
  );

  const deleteProfile = React.useCallback(
    (profileId: number): Promise<void> => {
      return new Promise((resolve) => {
        const doRequest = () => {
          makeRequest(
            `profiles/${profileId}/`,
            "DELETE",
            RetryBehavior.RETRY_ALL_CANCELLABLE,
            true
          ).then(() => {
            resolve();
          });
        };
        doRequest();
      });
    },
    [makeRequest]
  );

  const deleteAccount =
    React.useCallback((): Promise<DeleteAccountResponse> => {
      return new Promise((resolve) => {
        const doRequest = () => {
          makeRequest(
            "current-user/",
            "DELETE",
            RetryBehavior.RETRY_ALL_CANCELLABLE,
            true
          ).then((resp) => {
            resolve(resp.body);
          });
        };
        doRequest();
      });
    }, [makeRequest]);

  const checkUserInput = React.useCallback(
    (
      firstName: string
    ): Promise<{ result: string; error_message?: string } | undefined> => {
      const promise = makeRequest(
        "check-user-input/",
        "POST",
        RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
        true,
        { first_name: firstName }
      );
      return promise
        .then((resp) => {
          return resp.body;
        })
        .catch(() => {
          return undefined;
        });
    },
    [makeRequest]
  );

  const previewPhonics = React.useCallback(
    (ipa: string, firstName?: string): Promise<ArrayBuffer> => {
      return makeRequest(
        "phonics_preview/",
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          ...(ipa && { ipa }),
          ...(firstName && { first_name: firstName, greeting: firstName }),
        }
      )
        .then((response) => response.blob())
        .then((blob: Blob) => blob.arrayBuffer());
    },
    [makeRequest]
  );

  const getTeacherAvatars = React.useCallback((): Promise<{
    teacher_avatars_info: TeacherAvatarInfo[];
  }> => {
    return makeRequest(
      "teacher-avatars/",
      "GET",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const createLessonExecution = React.useCallback(
    (
      lessonId: number,
      lessonVersion: number,
      profileId: number
    ): Promise<{ lesson_execution_id: string }> => {
      return makeRequest(
        `game_lessons/${lessonId}/executions/`,
        "POST",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        {
          lesson_version: lessonVersion,
          profile_id: profileId,
          device_model_started_on: "",
          app_version_started_on: "",
        }
      ).then((resp) => {
        return resp.body;
      });
    },
    [makeRequest]
  );

  // "maybe" in the name because we don't retry failures and
  // use a boolean to let the caller know whether the update succeeded
  const maybeUpdateLessonExecution = React.useCallback(
    (
      lessonId: number,
      lessonExecutionId: string,
      deltaLengthSeconds: number,
      didComplete: boolean
    ): Promise<boolean> => {
      return makeRequest(
        `game_lessons/${lessonId}/executions/${lessonExecutionId}/`,
        "PATCH",
        RetryBehavior.EXPOSE_ALL,
        true,
        {
          delta_length_seconds: Math.floor(deltaLengthSeconds),
          did_complete: didComplete,
        }
      )
        .then((resp) => {
          return resp.isSuccessful;
        })
        .catch(() => {
          return false;
        });
    },
    [makeRequest]
  );

  const getCustomFlashpack = React.useCallback(
    (customFlashpackId: number) => {
      return new Promise<CustomFlashpack>((resolve) => {
        const doRequest = () => {
          makeRequest(
            `flashpacks/${customFlashpackId}/`,
            "GET",
            RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
            true
          ).then((resp) => {
            if (resp.body?.result === "ok") {
              resolve(resp.body.flashpack);
            } else {
              showRetry(doRequest);
            }
          });
        };
        doRequest();
      });
    },
    [makeRequest, showRetry]
  );

  const modifyCustomFlashpack = React.useCallback(
    (customFlashpackId: number, data: ModifyCustomFlashpackRequest) => {
      return makeRequest(
        `flashpacks/${customFlashpackId}/`,
        "PATCH",
        RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        data
      );
    },
    [makeRequest]
  );

  const createCustomFlashpack = React.useCallback(
    (
      flashpack_type: FlashpackType,
      profile_id: number,
      items: string[]
    ): Promise<CreateCustomFlashpackResponse> => {
      // Retry all errors until we decide what to do with non-retriable ones etc.
      return new Promise<CreateCustomFlashpackResponse>((resolve) => {
        const doRequest = () => {
          makeRequest(
            "flashpacks/",
            "POST",
            RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
            true,
            {
              flashpack_type,
              profile_id,
              items,
            }
          ).then((resp) => {
            if (resp.body?.result === "ok") {
              resolve(resp.body);
            } else {
              showRetry(doRequest);
            }
          });
        };
        doRequest();
      });
    },
    [makeRequest, showRetry]
  );

  const deleteCustomFlashpack = React.useCallback(
    (customFlashpackId: number): Promise<void> => {
      return new Promise((resolve) => {
        const doRequest = () => {
          makeRequest(
            `flashpacks/${customFlashpackId}/`,
            "DELETE",
            RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
            true
          ).then((resp) => {
            if (resp.body?.result === "ok") {
              resolve();
            } else {
              showRetry(doRequest);
            }
          });
        };
        doRequest();
      });
    },
    [makeRequest, showRetry]
  );

  const getFlashpackSubjects = React.useCallback((): Promise<{
    subjects: FlashpackSubject[];
  }> => {
    return makeRequest(
      "flashpack-subjects/",
      "GET",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const getFlashpackItems = React.useCallback(
    (flashpackType: FlashpackType): Promise<FlashpackItem[]> => {
      // Retry all errors until we decide what to do with non-retriable ones etc.
      return new Promise<FlashpackItem[]>((resolve) => {
        const doRequest = () => {
          makeRequest(
            `flashpack-items/${flashpackType}/`,
            "GET",
            RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
            true
          ).then((resp) => {
            if (resp.body.result === "ok") {
              resolve(resp.body.items);
            } else {
              showRetry(doRequest);
            }
          });
        };
        doRequest();
      });
    },
    [makeRequest, showRetry]
  );

  const getLessonRecordings = React.useCallback((): Promise<
    LessonRecording[]
  > => {
    // Retry all errors until we decide what to do with non-retriable ones etc.
    return new Promise<LessonRecording[]>((resolve) => {
      const doRequest = () => {
        makeRequest(
          "lessons/recordings/review/",
          "GET",
          RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
          true
        ).then((resp) => {
          resolve(resp.body.recordings);
        });
      };
      doRequest();
    });
  }, [makeRequest]);

  const deleteLessonRecording = React.useCallback(
    (recordingId: number): Promise<void> => {
      return new Promise((resolve) => {
        const doRequest = () => {
          makeRequest(
            `lessons/recordings/review/${recordingId}/`,
            "DELETE",
            RetryBehavior.RETRY_ALL_CANCELLABLE,
            true
          ).then(() => {
            resolve();
          });
        };
        doRequest();
      });
    },
    [makeRequest]
  );

  const modifyUser = React.useCallback(
    (
      receivesPostLessonEmails: boolean,
      recordLessonReplays: boolean,
      dontRetryErrors: boolean
    ): Promise<User | undefined> => {
      const path = "current-user/";
      const body = {
        receives_post_lesson_emails: receivesPostLessonEmails,
        record_lesson_replays: recordLessonReplays,
      };
      const promise = makeRequest(
        path,
        "PATCH",
        dontRetryErrors
          ? RetryBehavior.EXPOSE_ALL
          : RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
        true,
        body
      );
      return promise
        .then((resp) => {
          return resp.body;
        })
        .catch(() => {
          return undefined;
        });
    },
    [makeRequest]
  );

  const getManageSubscriptionUrl = React.useCallback(() => {
    return new Promise<ManageSubscriptionUrlResponse>((resolve) => {
      const doRequest = () => {
        makeRequest(
          "purchase/manage-subscription/",
          "GET",
          RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
          true
        ).then((resp) => {
          if (resp.body) {
            resolve(resp.body);
          } else {
            showRetry(doRequest);
          }
        });
      };
      doRequest();
    });
  }, [makeRequest, showRetry]);

  const getAvailableSubscriptions = React.useCallback((): Promise<{
    available_subscriptions: StripeSubscriptionProduct[];
  }> => {
    return makeRequest(
      "stripe/available-subscriptions/",
      "GET",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const createStripeCheckoutSession = React.useCallback(
    (
      productId: StripeSubscriptionProductId
    ): Promise<StripeCheckoutSession> => {
      return new Promise<StripeCheckoutSession>((resolve) => {
        makeRequest(
          "stripe/checkout/",
          "POST",
          RetryBehavior.RETRY_ALL_CANCELLABLE,
          true,
          { product_id: productId }
        ).then((resp) => {
          resolve(resp.body);
        });
      });
    },
    [makeRequest]
  );

  const reconcileStripeCheckoutSession = React.useCallback(
    (
      checkoutSessionId: string,
      bakery: Record<string, string>,
      eventSourceUrl: string
    ): Promise<ReconcileStripeCheckoutSessionResult> => {
      return new Promise<ReconcileStripeCheckoutSessionResult>((resolve) => {
        makeRequest(
          "stripe/checkout/reconcile/",
          "POST",
          RetryBehavior.RETRY_ALL_CANCELLABLE,
          true,
          {
            checkout_session_id: checkoutSessionId,
            bakery,
            event_source_url: eventSourceUrl,
          }
        ).then((resp) => {
          resolve(resp.body);
        });
      });
    },
    [makeRequest]
  );

  const resetPassword = React.useCallback(
    (email: string): Promise<void> => {
      return new Promise((resolve) => {
        const doRequest = () => {
          makeRequest(
            `auth/password/reset/`,
            "POST",
            RetryBehavior.RETRY_ALL_CANCELLABLE,
            true,
            { email: email }
          ).then(() => {
            resolve();
          });
        };
        doRequest();
      });
    },
    [makeRequest]
  );

  const sendFeedback = React.useCallback(
    (data: FeedbackRequest): Promise<void> => {
      return new Promise((resolve) => {
        const doRequest = () => {
          makeRequest(
            `feedback/`,
            "POST",
            RetryBehavior.RETRY_ALL_CANCELLABLE,
            true,
            data
          ).then(() => {
            resolve();
          });
        };
        doRequest();
      });
    },
    [makeRequest]
  );

  const getTeacherResources = React.useCallback((): Promise<{
    email_from_teacher_to_parents_path: string;
  }> => {
    return makeRequest(
      "teacher-resources/",
      "GET",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const getBakery = React.useCallback((): Promise<Record<string, string>> => {
    return makeRequest(
      "bakery/",
      "POST",
      RetryBehavior.HANDLE_40X_RETRY_OTHER_CANCELLABLE,
      true
    ).then((resp) => {
      return resp.body;
    });
  }, [makeRequest]);

  const search = React.useCallback(
    (
      q: string,
      flags: { [key: string]: string }
    ): Promise<SearchResponse | undefined> => {
      // Flags ride the query string like getCourses so search serves the same
      // universe as the courses screen (e.g. rct_arm swaps the main units).
      const queryString = new URLSearchParams(flags).toString();
      const path = queryString ? `search/?${queryString}` : "search/";
      return makeRequest(path, "POST", RetryBehavior.EXPOSE_ALL, true, {
        q,
      })
        .then((resp) => (resp.isSuccessful ? resp.body : undefined))
        .catch(() => undefined);
    },
    [makeRequest]
  );

  const getLessonStandards = React.useCallback(
    (lessonId: number): Promise<LessonStandardsResponse> => {
      // EXPOSE_ALL: the standards modal is an optional panel — a failure
      // surfaces a local error + retry there, never the global retry overlay.
      return makeRequest(
        `lessons/${lessonId}/standards/`,
        "GET",
        RetryBehavior.EXPOSE_ALL,
        true
      ).then((resp) => resp.body);
    },
    [makeRequest]
  );

  const getStandardsAttributions = React.useCallback(
    (): Promise<StandardsAttributionsResponse> => {
      // EXPOSE_ALL: the attributions page shows a local error + retry, never
      // the global retry overlay.
      return makeRequest(
        "standards/attributions/",
        "GET",
        RetryBehavior.EXPOSE_ALL,
        true
      ).then((resp) => resp.body);
    },
    [makeRequest]
  );

  const submitLessonStandardsFeedback = React.useCallback(
    (lessonId: number, feedback: string): Promise<boolean> => {
      // EXPOSE_ALL (no auto-retry): a silently retried POST would store the
      // same feedback twice; the form surfaces the failure and the user
      // retries explicitly.
      return makeRequest(
        `lessons/${lessonId}/standards/feedback/`,
        "POST",
        RetryBehavior.EXPOSE_ALL,
        true,
        { feedback }
      )
        .then((resp) => resp.isSuccessful)
        .catch(() => false);
    },
    [makeRequest]
  );

  const submitSearchFeedback = React.useCallback(
    (searchId: number, feedback: string): Promise<boolean> => {
      // EXPOSE_ALL (no auto-retry): a silently retried POST would store the
      // same feedback twice; the form surfaces the failure and the user
      // retries explicitly.
      return makeRequest("search/feedback/", "POST", RetryBehavior.EXPOSE_ALL, true, {
        id: searchId,
        feedback,
      })
        .then((resp) => resp.isSuccessful)
        .catch(() => false);
    },
    [makeRequest]
  );

  const contextValue = React.useMemo(
    () => ({
      getCourses,
      getPracticeMetadata,
      getHomeMetadata,
      getStudentAssignmentsMetadata,
      getLeaderboards,
      getAwardsMetadata,
      getMilestonesMetadata,
      getMyClassMetadata,
      getUnitWithLessons,
      obtainNextLesson,
      getLesson,
      deleteLessonState,
      makeGuestUser,
      getTeacherSections,
      getTeachers,
      createCustomSection,
      updateCustomSection,
      deleteCustomSection,
      getCustomSection,
      getCustomSections,
      getSchoolDashboard,
      getSchoolList,
      getSchoolGroupList,
      getSchoolRenewalList,
      getSchoolInsightReports,
      createLessonAssignments,
      getSingleLessonAssignments,
      deleteSingleLessonAssignments,
      modifyProfile,
      createProfile,
      deleteProfile,
      deleteAccount,
      checkUserInput,
      previewPhonics,
      getTeacherAvatars,
      createLessonExecution,
      maybeUpdateLessonExecution,
      getCustomFlashpack,
      modifyCustomFlashpack,
      createCustomFlashpack,
      deleteCustomFlashpack,
      getFlashpackSubjects,
      getFlashpackItems,
      getLessonRecordings,
      deleteLessonRecording,
      modifyUser,
      getManageSubscriptionUrl,
      getAvailableSubscriptions,
      createStripeCheckoutSession,
      reconcileStripeCheckoutSession,
      resetPassword,
      sendFeedback,
      getBakery,
      getTeacherResources,
      search,
      submitSearchFeedback,
      getLessonStandards,
      submitLessonStandardsFeedback,
      getStandardsAttributions,
    }),
    [
      getCourses,
      getPracticeMetadata,
      getHomeMetadata,
      getStudentAssignmentsMetadata,
      getLeaderboards,
      getAwardsMetadata,
      getMilestonesMetadata,
      getMyClassMetadata,
      getUnitWithLessons,
      obtainNextLesson,
      getLesson,
      deleteLessonState,
      makeGuestUser,
      getTeacherSections,
      getTeachers,
      createCustomSection,
      updateCustomSection,
      deleteCustomSection,
      getCustomSection,
      getCustomSections,
      getSchoolDashboard,
      getSchoolList,
      getSchoolGroupList,
      getSchoolRenewalList,
      getSchoolInsightReports,
      createLessonAssignments,
      getSingleLessonAssignments,
      deleteSingleLessonAssignments,
      modifyProfile,
      createProfile,
      deleteProfile,
      deleteAccount,
      checkUserInput,
      previewPhonics,
      getTeacherAvatars,
      createLessonExecution,
      maybeUpdateLessonExecution,
      getCustomFlashpack,
      modifyCustomFlashpack,
      createCustomFlashpack,
      deleteCustomFlashpack,
      getFlashpackSubjects,
      getFlashpackItems,
      getLessonRecordings,
      deleteLessonRecording,
      modifyUser,
      getManageSubscriptionUrl,
      getAvailableSubscriptions,
      createStripeCheckoutSession,
      reconcileStripeCheckoutSession,
      resetPassword,
      sendFeedback,
      getBakery,
      getTeacherResources,
      search,
      submitSearchFeedback,
      getLessonStandards,
      submitLessonStandardsFeedback,
      getStandardsAttributions,
    ]
  );

  return (
    <ApiContext.Provider value={contextValue}>
      {props.children}
    </ApiContext.Provider>
  );
};
